// The details card at the top of the Prompt Editor (Admin › AI Prompts): the
// module, handle (prompt key), name, one-line summary and test of one prompt.
// For a new record it also takes the model and the first Markdown, and creates
// the prompt with a v1 Draft. The handle is what code uses to find the prompt,
// so it can't change once created.

// REACT IMPORTS
import React, { useEffect, useState } from "react";

// MSAL
import { useMsal } from "@azure/msal-react";

// MUI COMPONENTS
import { Alert, Autocomplete, Button, CircularProgress, FormControlLabel, MenuItem, Switch, TextField } from "@mui/material";

// API, CONSTANTS
import { getAPICall, patchAPICall, postAPICall } from "../../config/apiCalls";
import { apiError } from "../ITBudget/itBudgetUtils";

const fromPrompt = (p) => ({ module: p.module, name: p.name, description: p.description || "", test_handler: p.test_handler || "", active: Boolean(p.active) });

const PromptDetails = ({ prompt, canEdit, modules, handlers, onSaved }) => {
	const { instance, accounts } = useMsal();
	const creating = !prompt;
	const [form, setForm] = useState(
		prompt ? fromPrompt(prompt) : { prompt_key: "", module: "", name: "", description: "", test_handler: "", ai_model_id: "", prompt_markdown: "" },
	);
	const [models, setModels] = useState([]);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const [saved, setSaved] = useState(false);
	const set = (field) => (e) => {
		setSaved(false);
		setForm({ ...form, [field]: e.target.value });
	};

	useEffect(() => {
		if (prompt) setForm(fromPrompt(prompt));
	}, [prompt]);

	useEffect(() => {
		if (!creating) return;
		getAPICall(instance, accounts[0], "/api/ai-prompts/models")
			.then((res) => setModels(res.data.filter((m) => m.active)))
			.catch(() => {});
	}, [creating]);

	const save = async () => {
		setBusy(true);
		setError("");
		try {
			const body = { ...form, test_handler: form.test_handler || null };
			const res = creating
				? await postAPICall(instance, accounts[0], "/api/ai-prompts/prompts", body)
				: await patchAPICall(instance, accounts[0], `/api/ai-prompts/prompts/${prompt.id}`, body);
			setSaved(!creating);
			onSaved(res.data.prompt);
		} catch (err) {
			setError(apiError(err, "Couldn't save the prompt."));
		} finally {
			setBusy(false);
		}
	};

	const locked = !canEdit;
	return (
		<div className="itBudgetCard" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
			<div className="itBudgetOverline">{creating ? "New module prompt" : "Details"}</div>
			{error && (
				<Alert severity="error" onClose={() => setError("")}>
					{error}
				</Alert>
			)}
			<div className="itBudgetFormGrid">
				<Autocomplete
					freeSolo
					disabled={locked}
					options={modules}
					value={form.module}
					onInputChange={(e, value) => {
						setSaved(false);
						setForm((f) => ({ ...f, module: value }));
					}}
					renderInput={(params) => <TextField {...params} size="small" label="Module" required helperText="The J2 Tools area that uses it, e.g. IT Budget" />}
				/>
				<TextField
					size="small"
					label="Handle"
					required
					value={creating ? form.prompt_key : prompt.prompt_key}
					disabled={!creating}
					onChange={set("prompt_key")}
					helperText={creating ? "module.name in lowercase, e.g. unit_mining.units; code uses it to find the prompt" : "Code finds the prompt by its handle, so it can't change"}
					inputProps={{ className: "itBudgetMono" }}
				/>
				<TextField size="small" label="Name" required value={form.name} disabled={locked} onChange={set("name")} />
				<TextField select size="small" label="Test" value={form.test_handler} disabled={locked} onChange={set("test_handler")} helperText="A test the module provides; without one there's no Test button">
					<MenuItem value="">No test</MenuItem>
					{handlers.map((h) => (
						<MenuItem key={h} value={h}>
							{h}
						</MenuItem>
					))}
				</TextField>
			</div>
			<TextField size="small" label="Summary" value={form.description} disabled={locked} onChange={set("description")} helperText="One line: what the prompt does, shown in the Modules list" />
			{creating && (
				<>
					<TextField select size="small" label="Model" required value={form.ai_model_id} onChange={set("ai_model_id")}>
						{models.map((m) => (
							<MenuItem key={m.id} value={m.id}>
								{m.name} ({m.provider} · {m.model_identifier})
							</MenuItem>
						))}
					</TextField>
					<TextField
						className="itBudgetMarkdownInput"
						label="Prompt (Markdown)"
						required
						multiline
						minRows={10}
						value={form.prompt_markdown}
						onChange={set("prompt_markdown")}
						helperText="Saved as Draft v1; the full editor (preview, schema, Test, Activate) opens once it's created"
						inputProps={{ spellCheck: false }}
					/>
				</>
			)}
			{canEdit && (
				<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
					{!creating && <FormControlLabel control={<Switch checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />} label="Active — code can use this prompt" />}
					<span style={{ marginLeft: "auto" }} className="itBudgetMuted">
						{saved ? "Saved" : ""}
					</span>
					<Button variant={creating ? "contained" : "outlined"} onClick={save} disabled={busy} startIcon={busy ? <CircularProgress size={16} /> : null}>
						{creating ? "Create Draft v1" : "Save Details"}
					</Button>
				</div>
			)}
		</div>
	);
};

export default PromptDetails;
